import type { Dispatch, SetStateAction } from 'react'

import { Box } from '@mantine/core'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { isEqual } from 'lodash-es'

import SelectionColumnCheckbox from './SelectionColumnCheckbox'
import { SELECT_COLUMN_ID } from '../../../constants'
import type { ValidRowSelection } from '../../../hooks/useRowSelection/use-row-selection'

const selectionColumn = <T extends Partial<Record<'id', string | number>>>({
  setSelectedRows,
  selectedRows,
  allIdsOnActivePage,
}: {
  setSelectedRows: Dispatch<SetStateAction<ValidRowSelection>>
  selectedRows: ValidRowSelection
  allIdsOnActivePage: (string | number)[]
}): ColumnDef<T, string | number> => {
  const columnHelper = createColumnHelper<T>()

  const selectionColumn = columnHelper.display({
    id: SELECT_COLUMN_ID,
    enableSorting: false,
    size: 30,
    minSize: 30,

    header: ({ table }) => {
      return <></>
      table.getIsAllRowsSelected()

      const headerChecked =
        allIdsOnActivePage.length > 0 &&
        isEqual(allIdsOnActivePage, selectedRows)

      const headerIndeterminate =
        selectedRows.length > 0 &&
        selectedRows.length < allIdsOnActivePage.length

      const headerChangeHandler = (_e: React.ChangeEvent<HTMLInputElement>) => {
        if (headerChecked) {
          setSelectedRows([])
        } else if (headerIndeterminate || selectedRows.length === 0) {
          setSelectedRows(allIdsOnActivePage)
        } else {
          setSelectedRows([])
        }
      }

      return (
        <SelectionColumnCheckbox
          indeterminate={headerIndeterminate}
          checked={headerChecked}
          onChange={headerChangeHandler}
        />
      )
    },
    cell: ({ row }) => {
      const rowId = row.getValue('id') as string | number

      if (!rowId) throw new Error('selectionColumn: rowId is invalid')

      // Calculate this inside the cell function to ensure it's fresh
      const allSelected =
        isEqual([...allIdsOnActivePage].sort(), [...selectedRows].sort()) &&
        selectedRows.length > 0

      const isRowSelected = selectedRows.includes(rowId) || allSelected

      const handleChange = () => {
        if (allSelected) {
          setSelectedRows(selectedRows.filter((id) => id !== rowId))
        } else if (isRowSelected) {
          setSelectedRows((current) => current.filter((id) => id !== rowId))
        } else {
          setSelectedRows((current) => [...current, rowId])
        }
      }

      return (
        <Box
          py="xs"
          px="md"
        >
          <SelectionColumnCheckbox
            checked={isRowSelected}
            indeterminate={false}
            onChange={handleChange}
          />
        </Box>
      )
    },
  })
  return selectionColumn
}

export default selectionColumn
